import * as React from "react";
import { AutoComplete, Tag } from "antd";
import { useKindSelectData } from "../utils/useKindSelectData";
import { Kind } from "../../../schema/types/base";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { linksOf } from "../../../services/cpln";
import { FilterTerm } from "../../../schema/types/query/query";
import { useAppSelector } from "../../../store/hooks";
import { selectContext } from "../../../store/contextSlice";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { gvcScopedKinds } from "../../../services/utils";

export interface KindSelectState {
  value: string;
  link: string;
  item?: any;
}

interface GetDefaultKindSelectStateProps {
  value?: string;
  link?: string;
}
export function getDefaultKindSelectState(props?: GetDefaultKindSelectStateProps) {
  return {
    value: props?.value || "",
    link: props?.link || "",
  };
}

export interface KindSelectProps {
  kind: Kind;
  link?: string;
  optionValue: "name" | "relative";
  optionRenderValue?: "name" | "relative";
  queries?: FilterTerm[];
  filterOptionsFn?: (item: any) => boolean;
  tagRenderers?: ((item: any) => string)[];
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  status?: "error" | "warning";
  id?: string;
  debug?: boolean;
  value: string;
  onChange: (value: string) => void;
  onStateChange?: (state: KindSelectState) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}
export const KindSelect = (props: KindSelectProps): React.ReactElement => {
  const context = useAppSelector(selectContext);
  const [showAll, setShowAll] = React.useState<boolean>(true);

  const {
    kind,
    optionValue,
    optionRenderValue = "name",
    queries = [],
    filterOptionsFn,
    tagRenderers,
    placeholder = "",
    className = "",
    style = {},
    disabled = false,
    id,
    status,
    debug,
    value,
    onChange,
    onStateChange,
    onFocus,
    onBlur,
  } = props;

  const data = useKindSelectData<any>(kind, queries);

  const [debugText, setDebugText] = React.useState("");
  const [link, setLink] = React.useState("");
  const [item, setItem] = React.useState<any>(undefined);

  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    process();
  }, [value]);

  React.useEffect(() => {
    if (focused) {
      return;
    }
    simplifyValue();
  }, [focused]);

  function process() {
    if (value === undefined) {
      return;
    }

    if (!value) {
      data.setSearch("");
      setLink("");
      setItem(undefined);
      onStateChange?.({
        link: "",
        value: "",
        item: undefined,
      });
      setDebugText(`no value`);
      return;
    }

    let gvc = queries.find((q) => q.rel === "gvc")?.value || context.gvc || "";
    if (value.includes(`/gvc/`)) {
      const gvcPart = value.split(`/gvc/`)[1];
      const valueGvc = gvcPart.split(`/`)[0];
      if (valueGvc) {
        gvc = valueGvc;
      }
    }
    if (kind === "gvc") {
      gvc = "";
    }

    let org = context.org || "";

    const ctx = { org: org, gvc: gvc };

    let parsedLink = parseLink(value, { kind: kind, org: ctx.org, gvc: ctx.gvc });
    if (value.startsWith(`/`)) {
      parsedLink = parseLink(value, { useInputCtx: true, kind: kind });
    }

    let newName = value;
    let newLink = link;
    let newItem = item;

    if (parsedLink.isValid) {
      newName = parsedLink.name;
      newLink = parsedLink.absolute;
      setDebugText(`parsed absolute: ${newLink}`);
      newItem = data.allItems.find((i) => {
        const selfLink = linksOf(i).self;
        return !!selfLink && selfLink === parsedLink.absolute;
      });
    } else {
      setDebugText(`not-parsed: ${value}`);
      newLink = value;
      newItem = undefined;
    }

    const newState: KindSelectState = {
      link: newLink,
      value: value,
      item: newItem,
    };

    data.setSearch(newName);
    setLink(newLink);
    setItem(newItem);
    onStateChange?.(newState);
  }

  function simplifyValue() {
    if (!value || !link) {
      return;
    }

    const parsedLink = parseLink(link, { useInputCtx: true });
    if (optionValue === "name") {
      onChange(parsedLink.name);
    } else {
      if (gvcScopedKinds.includes(kind)) {
        onChange(parsedLink.gvcRelative);
      } else {
        onChange(parsedLink.relative);
      }
    }
  }

  const getOptionRenderValue: (item: any) => string = (item) => {
    let value = item.name;
    if (optionRenderValue === "relative") {
      const selfLink = linksOf(item).self!;
      const { isGVCScoped, relative, gvcRelative } = parseLink(selfLink, { useInputCtx: true });
      value = isGVCScoped ? gvcRelative : relative;
    }
    return value;
  };

  const getOptionValue: (item: any) => string = (item) => {
    let value = item.name;
    if (optionValue === "relative") {
      const selfLink = linksOf(item).self!;
      const { isGVCScoped, relative, gvcRelative } = parseLink(selfLink, { useInputCtx: true });
      value = isGVCScoped ? gvcRelative : relative;
    }
    return value;
  };

  if (optionValue === "name" && gvcScopedKinds.includes(kind) && !queries.some((q) => q.rel === "gvc")) {
    return (
      <div className="color-danger">
        KindSelect component is unable to use optionValue={"name"} when "kind" is gvc scoped and there is no restricted
        kind information in queries.
      </div>
    );
  }

  if (kind === "org") {
    return (
      <div className="color-danger">
        KindSelect is not a good component to use for selecting org. Use AutoComplete by feeding orgs separately
      </div>
    );
  }

  return (
    <>
      {debug ? (
        <div className="text-xs border px-2 py-1 flex flex-col my-2">
          <div>option {optionValue}</div>
          <div>value {value}</div>
          <div>link {link}</div>
          <div>item?.name {item?.name}</div>
          <div>item?.kind {item?.kind}</div>
          <div>{debugText}</div>
        </div>
      ) : null}
      <AutoComplete
        id={id}
        disabled={disabled}
        status={status}
        onOpenChange={(visible) => {
          if (!visible) {
            setShowAll(true);
          }
        }}
        value={value}
        onChange={(value: string) => {
          // console.log("onchange", value);
          onChange(value);
        }}
        onSearch={(value: string) => {
          if (!value) {
            setShowAll(true);
          } else {
            setShowAll(false);
          }
        }}
        onFocus={() => {
          setFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
        suffixIcon={
          <div className="flex items-center gap-1">
            {data.isLoading ? <LoadingOutlined /> : null}
            <DownOutlined style={{ pointerEvents: "none" }} />
          </div>
        }
        placeholder={placeholder}
        className={`${className.includes("w-") ? "" : "w-full"} ${className}`}
        style={style}
        filterOption={() => true}
        notFoundContent={<div>No items found for this search</div>}
      >
        {data.allItems.map((item) => {
          if (filterOptionsFn) {
            const result = filterOptionsFn(item);
            if (!result) {
              return null;
            }
          }

          const valueOfOption = getOptionValue(item);

          let shouldDisplay = true;
          if (!showAll) {
            if (item.meta.source === "default") {
              shouldDisplay = false;
            }
          } else if (showAll) {
            if (item.meta.source === "query") {
              shouldDisplay = false;

              if (!!value && valueOfOption === value) {
                const alsoInNonQueried = data.allItems
                  .filter((i) => i.meta.source === "default")
                  .some((i) => linksOf(i).self === link);
                if (!alsoInNonQueried) {
                  shouldDisplay = true;
                }
              }

              if (!shouldDisplay) {
              }
            }
          }

          if (!shouldDisplay) {
            return null;
          }

          return (
            <AutoComplete.Option key={valueOfOption} value={valueOfOption}>
              <div className="flex justify-between">
                {getOptionRenderValue(item)}
                <div>
                  {tagRenderers
                    ? tagRenderers.map((t) => {
                        const tagValue = t(item);
                        if (!tagValue) {
                          return null;
                        }
                        return (
                          <Tag key={tagValue} className="text-xs">
                            {tagValue}
                          </Tag>
                        );
                      })
                    : null}
                </div>
              </div>
            </AutoComplete.Option>
          );
        })}
      </AutoComplete>
    </>
  );
};

// kind select
// takes value and onChange akways, controlled component

// optionValue is name
// fail with an error mesasge if kind is gvc scoped
// process on change
// - from possible link to name, if fails to parse, give as is to value
// - construct link and name, if fails to be valid link, use value
// - set item if found by link

// optionValue is relative
// if kind is gvc scoped, valid formats
// - //gvc/name/kind/name
// - /org/name/gvc/name/kind/name

// if kind is org scoped, values formats
// valid values are
// - name
// - //kind/name
// - /org/name/kind/name

// process on change
// - from possible link to name, if fails to parse, give as is to value
// - construct link and name, if fails to be valid link, use value
// - set item if found by link
